/* ----------------- Constants --------------- */
import { events } from "@constants/tracking";
import type { EXAM_STATUS } from "types/tag";

/* ----------------- React & Hooks --------------- */
import { useState } from "react";

/* ----------------- Features --------------- */
import useSessionStore from "@features/session/hooks/useSessionStore";

/* ----------------- Utils --------------- */
import { useRouter } from "expo-router";

import { flattenSessionItems } from "@features/session/helpers/session";
import { useResumeSession } from "@features/session/hooks/useResumeSession";
import { useSession } from "@features/session/hooks/useSession";
import { getIsOlxExam } from "@helpers/exams";
import useMixedItemDist from "@hooks/useMixedItemDist";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { isWeb } from "@helpers/platform";
import { useToast } from "@memorang/ui/components/Toast";
/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";
export const usePracticeTests = () => {
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const [selectedExamId, setSelectedExamId] = useState<string | undefined>();

	const [showResumeSessionInfoDialog, setShowResumeSessionInfoDialog] =
		useState(false);
	const [selectedNumericId, setSelectedNumericId] = useState<
		number | undefined
	>();
	const [creatingSession, setCreatingSession] = useState(false);
	const [showErrorDialog, setShowErrorDialog] = useState(false);

	const { showToast } = useToast();

	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();

	const [showCollectUserDetailsDialog, setShowCollectUserDetailsDialog] =
		useState(false);

	const [olxError, setOlxError] = useState<{
		title: string;
		message: string;
	} | null>(null);

	const [olxExamEligibility, setOlxExamEligibility] = useState<{
		endDate: string;
		startDate: string;
	} | null>(null);

	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);

	const sessionNumericId = useSessionStore((state) => state.numericId);

	const toggleShowCollectUserDetailsDialog = () => {
		setShowCollectUserDetailsDialog((prev) => !prev);

		router.push({
			pathname: "/(protected)/session/[id]",
			params: {
				id: sessionNumericId,
				index: "1",
			},
		});
	};

	const { handleResumeSession } = useResumeSession();

	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const router = useRouter();
	const {
		data: mixedItemDistResponse,
		isLoading,
		isRefetching,
	} = useMixedItemDist(true, "GET_PRATICETEST_DISTRIBUTION");

	const { createNewSession } = useSession();

	const hasProductAccess = mixedItemDistResponse?.hasAccess;

	const handleStartSession = async (
		examId: string,
		numericId?: number,
		isQuiz?: boolean,
		isOlx?: boolean,
		replace = true,
		collectionId?: number,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	) => {
		setSelectedExamId(examId);
		setCreatingSession(true);
		try {
			if (isQuiz) {
				trackCustomEvent({
					eventName: events.sessionDiagnosticClicked,
					numericId,
				});
			}

			const params = {
				collectionId: collectionId!,
				projectId,
				organizationId,
				sessionType: "SUMMATIVE" as const,
				sessionSubType: isQuiz ? "DIAGNOSTIC" : "PRACTICE_TEST",
			} as const;

			const result = await createNewSession(params);
			trackCustomEvent({
				eventName: events.sessionStarted,
				type: "summative",
				numericId,
			});

			if (result.errors) {
				setOlxError({
					title: result.errors.title,
					message: result.errors.message,
				});
				return;
			}
			if (!result.sessionItems?.length) {
				showToast({
					message: "Failed to create session, try again",
				});
				return;
			}

			const flattenedSessionItems = flattenSessionItems(result.sessionItems);

			const newSessionResponse = {
				...result,
				sessionItems: flattenedSessionItems,
			};

			updateSession({
				sessionResponse: newSessionResponse,
				isSummativeTest: true,
				isDiagnostic: false,
			});
			if (isOlx) {
				setShowCollectUserDetailsDialog(true);
			} else {
				const routerFunction = replace ? router.replace : router.push;
				routerFunction({
					pathname: "/(protected)/session/[id]",
					params: {
						id: result.numericId,
						index: "1",
					},
				});
			}
		} catch (_error: unknown) {
			setShowErrorDialog(true);
		} finally {
			setSelectedExamId(undefined);
			setCreatingSession(false);
		}
	};
	const handleActiveSession = async (examId: string, numericId?: number) => {
		if (numericId) {
			setSelectedExamId(examId);
			setCreatingSession(true);
			await handleResumeSession(numericId);
			setSelectedExamId(undefined);
			setSelectedNumericId(undefined);
			setShowResumeSessionInfoDialog(false);
			setCreatingSession(false);
		}
	};
	const handlePracticeTestSession = async (
		examId: string,
		status: EXAM_STATUS,
		numericId?: number,
		isQuiz?: boolean,
		hasExamAccess?: boolean,
		replace?: boolean,
		collectionId?: number,
		shouldStartSession?: boolean,
		isRecommendedTask?: boolean,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	) => {
		resetSessionStore();
		switch (status) {
			case "COMPLETE":
			case "LEGACYREPORT":
			case "MIGRATEDREPORT":
				// handleStartSession(examId, numericId, isQuiz);
				router.push(`/session/${numericId}/report/`);
				break;
			case "ACTIVE":
				{
					if (isRecommendedTask) {
						handleActiveSession(examId, numericId);
					} else {
						setSelectedNumericId(numericId);
						setShowResumeSessionInfoDialog(true);
					}
				}
				break;
			case "NOT_STARTED":
				if (
					(hasExamAccess && hasProductAccess) ||
					isQuiz ||
					shouldStartSession
				) {
					await handleStartSession(
						examId,
						numericId,
						isQuiz,
						getIsOlxExam(examId),
						replace,
						collectionId,
					);
				} else {
					if (examId.includes("OLX")) {
						router.push("/redeem");
						return;
					}
					if (isWeb) {
						setShowPurchaseDialog(true);
					} else {
						router.push({
							pathname: "/purchase",
							params: {
								productType: "PRACTICE_TEST",
							},
						});
					}
				}
				break;
			default:
				break;
		}
	};

	return {
		mixedItemDistResponse,
		handlePracticeTestSession,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		selectedExamId,
		creatingSession,
		showErrorDialog,
		setShowErrorDialog,
		loading: isLoading || isRefetching,
		olxExamEligibility,
		setOlxExamEligibility,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
		selectedNumericId,
		showResumeSessionInfoDialog,
		setShowResumeSessionInfoDialog,
		handleActiveSession,
	};
};
